<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import { gsap } from "gsap";
import { useGlobalStore } from "@stores/global";
import { useAchievementsStore } from "@stores/achievements";
import AchievementCard from "@components/achievements/Card.vue";
import IconX from "@components/icons/X.vue";

const globalStore = useGlobalStore();
const store = useAchievementsStore();

const items = computed(() => store.definitions);

const completedCount = computed(
    () => items.value.filter((def) => store.isComplete(def.slug)).length,
);
const totalCount = computed(() => items.value.length);
const overallPercent = computed(() =>
    totalCount.value === 0
        ? 0
        : Math.round((completedCount.value / totalCount.value) * 100),
);

function close() {
    globalStore.setPage("explore");
}

const rootEl = ref(null);

function playReveal() {
    if (!rootEl.value) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const targets = rootEl.value.querySelectorAll("[data-reveal]");
    if (!targets.length) return;

    gsap.killTweensOf(targets);
    gsap.fromTo(
        targets,
        { opacity: 0, y: 16 },
        {
            opacity: 1,
            y: 0,
            duration: 0.5,
            ease: "power2.out",
            stagger: 0.06,
        },
    );
}

onMounted(async () => {
    await nextTick();
    playReveal();
});
</script>

<template>
    <Transition name="panel" appear>
        <div
            ref="rootEl"
            class="achievements-sidebar bg-marine-800 border-marine-500 flex flex-col gap-6 border-l"
        >
            <div data-reveal class="flex items-center justify-between p-6">
                <p class="h6 leading-[0.9] font-bold tracking-tight uppercase">
                    Achievements
                </p>
                <button
                    class="border-marine-600 shadow-marine-600 flex size-8 items-center justify-center rounded-[4px] border-2 bg-yellow-400 shadow-[0_4px_0]"
                    @click="close"
                >
                    <IconX class="text-marine-600 size-3" />
                </button>
            </div>

            <div data-reveal class="flex flex-col gap-4 px-6">
                <div class="flex items-center justify-between">
                    <p class="body-sm leading-[0.8] font-bold uppercase">
                        {{ completedCount }}/{{ totalCount }} Achievements
                    </p>
                    <p
                        class="body-sm leading-[0.8] font-bold text-yellow-400 uppercase"
                    >
                        {{ overallPercent }}%
                    </p>
                </div>
            </div>

            <div class="flex flex-1 flex-col gap-6">
                <AchievementCard
                    v-for="def in items"
                    :key="def.slug"
                    :slug="def.slug"
                    data-reveal
                />
            </div>
        </div>
    </Transition>
</template>

<style scoped>
.achievements-sidebar {
    grid-column: -6 / -1;
    grid-row: 2 / -1;
    z-index: 2;
    overflow-y: auto;
}

@media (max-width: 1024px) {
    .achievements-sidebar {
        grid-column: 1 / -1;
        grid-row: -2 / -1;
        align-self: end;
        height: 50svh;
        max-height: 50svh;
        z-index: 10;
    }
}
</style>
